import { addDoc, collection, doc, getDocs, query, setDoc, where } from 'firebase/firestore'

import { db } from 'utils/firebase'

import { PendingMembership } from 'types/pendingMemberships'
import { Program } from 'types/program'

import { formatQuerySnapshot } from './formatFirestoreResults'

const DOC = 'pendingMemberships'

/**
 * Gets the pending memberships for a user based on the ID supplied.
 * @param userId The user ID to fetch the pending membership for.
 * @returns All pending memberships for a user.
 */
export const fetchPendingMembershipsByUserId = async (
  userId?: string,
): Promise<Array<PendingMembership> | null> => {
  if (!userId) {
    throw new Error('No user ID passed into fetchPendingMembershipsByUserId')
  }

  const baseQuery = query(
    collection(db, DOC),
    where('userId', '==', userId),
    where('status', '==', 'pending'),
  )
  const querySnapshot = await getDocs(baseQuery)
  return formatQuerySnapshot(querySnapshot)
}

export const fetchPendingMembershipsByUserIdAndProgramId = async (
  userId?: string,
  programId?: string,
): Promise<Array<PendingMembership> | null> => {
  if (!userId) {
    throw new Error('No user ID passed into fetchPendingMembershipsByUserIdAndProgramId')
  }

  const baseQuery = query(
    collection(db, DOC),
    where('userId', '==', userId),
    where('program.id', '==', programId),
    where('status', '==', 'pending'),
  )
  const querySnapshot = await getDocs(baseQuery)
  return formatQuerySnapshot(querySnapshot)
}

/**
 * Adds a pending membership for a user if one does not already exist.
 * @param userId The User's ID to add the pending membership for.
 * @param program The program to add the pending membership for.
 */
export const addPendingMembershipForUserId = async (userId: string, program: Program) => {
  const pendingMembership: PendingMembership = {
    program: {
      id: program.id,
      trainer: {
        id: program.trainer.id,
      },
    },
    updated: Date.now(),
    status: 'pending',
    userId,
  }

  if ((await fetchPendingMembershipsByUserIdAndProgramId(userId, program.id))?.length === 0) {
    await addDoc(collection(db, 'pendingMemberships'), pendingMembership)
  }
}

export const cancelPendingMembershipByUserIdAndProgramId = async (
  userId: string,
  programId: string,
) => {
  const baseQuery = query(
    collection(db, DOC),
    where('userId', '==', userId),
    where('program.id', '==', programId),
  )

  const querySnapshot = await getDocs(baseQuery)
  const results = formatQuerySnapshot(querySnapshot)
  if (results.length === 0) return null

  const dataToUpdate = { status: 'canceled', updated: Date.now() }
  await setDoc(doc(db, DOC, results[0].id), dataToUpdate, { merge: true })
  return dataToUpdate
}
